import { collectProperties } from 'components/appInsight';
import { Layout } from 'components/Layout';
import PageContent from 'components/page-content/sport-disciplina-manifestazione/PageContent';
import { Seo } from 'components/seo';
import { mergeTagsFromLabels, parseSlugFromParams } from 'components/seo';
import { loadFlat, loadIndex } from 'components/staticMap';
import { AmbienteContext } from 'context/Ambiente';
import { AppFragment } from 'enums/path-fragment';
import { sportSlice } from 'features/sport/sportSlice';
import { getSportDisciplinaPage, SportDisciplinaPageQuery } from 'lib/datoCms/queries/getSportDisciplinaPage';
import { getSportManifestazionePage } from 'lib/datoCms/queries/getSportManifestazione';
import { AllCarrelloErrors, DatoCmsSportManifestazionePage } from 'lib/datoCms/types';
import { cleanConfig } from 'lib/datoCms/utils';
import { serverSideAppInsight } from 'lib/serverAppInsight/serverAppInsight';
import { preloadVisualComposerData, useVisualComposerSnaphot } from 'lib/ssr';
import { GetStaticPathsResult } from 'next';
import { getDatoCMSData } from 'sites/snai/lib/datoCms/queries/getDatoCMSData';
import { SportPageProps } from 'sites/snai/pages/sport';
import { SPORT_REVALIDATE } from 'utility/constant';
import { isMatch, skipStaticPageGeneration } from 'utility/functions';

interface ManifestazionePageProps extends Omit<SportPageProps, 'cmsContent'> {
  cmsContent: SportDisciplinaPageQuery;
  manifestazioneDatoCms: DatoCmsSportManifestazionePage;
  allCarrelloErrors: Array<AllCarrelloErrors>;
}

export async function getStaticPaths() {
  const result: GetStaticPathsResult<{ disciplina: string; manifestazione: string }> = {
    paths: [],
    fallback: 'blocking',
  };

  if (!skipStaticPageGeneration()) {
    await loadIndex();

    // result.fallback = true;
    loadFlat(AppFragment.Sport, 3)
      .filter((x) => !isMatch(x.loc, `^(/?)${AppFragment.Sport}/(info|marcatori)/`))
      .forEach(({ loc }) => {
        const [dis, man] = `${loc}`.replace(`/${AppFragment.Sport}/`, '').split('/');

        if (!isMatch(man, '^primo-piano$')) {
          const disciplina = encodeURIComponent(dis.toUpperCase());
          const manifestazione = encodeURIComponent(man.toUpperCase());
          // sport pages are indexed by SEO as /sport/CALCIO/xxx
          result.paths.push({ params: { disciplina, manifestazione } });
        }
      });

    console.log(`## sport.manifestazione.getStaticPaths -> ${result.paths.length} items`);
  }

  return result;
}

export const getStaticProps = async ({ locale, params }) => {
  const { disciplina, manifestazione } = params ?? {};

  const slug = parseSlugFromParams(`${AppFragment.Sport}/${disciplina}/${manifestazione}`);
  console.log(`## sport.Manifestazione.getStaticProps ${slug}`);

  try {
    const prCmsData = getDatoCMSData('common', locale);
    const prDisciplinaData = getSportDisciplinaPage(slug, locale);

    const [cmsData, disciplinaPage] = await Promise.all([prCmsData, prDisciplinaData]);
    const { site, header, footer, needHelp, allLabels: labels, sidebarInstantGame, config } = cmsData ?? {};

    const prSSR = preloadVisualComposerData({
      slug,
      locale,
      visualComposer: disciplinaPage?.sportDisciplinaPage?.visualComposer ?? [],
      isManifestazione: true, // load temaplte manifestazione
    });
    const prManifestazioneData = getSportManifestazionePage(locale);

    const [ssrData, manifestazioneData] = await Promise.all([prSSR, prManifestazioneData]);

    const { allMightLikes, tabsWithAccordionModel, ...disciplinaContent } = disciplinaPage ?? {};
    const { allCarrelloErrors, ...manifestazioneDatoCms } = manifestazioneData ?? {};

    const cmsContent = mergeTagsFromLabels({
      slug,
      labels,
      cmsData: disciplinaContent,
      pageType: 'manifestazione',
      fieldName: 'sportDisciplinaPage',
    });

    return Promise.resolve({
      props: {
        slug,
        site,
        footer,
        labels,
        header,
        config: cleanConfig(config),
        seoData: tabsWithAccordionModel ?? null,
        needHelp,
        mightLikes: allMightLikes ?? null,
        cmsContent,
        sidebarInstantGame,
        allCarrelloErrors,
        manifestazioneDatoCms,
        ...ssrData,
      },
      revalidate: SPORT_REVALIDATE,
    });
  } catch (exception) {
    serverSideAppInsight.defaultClient.trackException({
      exception,
      properties: collectProperties(true, {
        id: `/${slug}/index`,
      }),
    });

    return Promise.resolve({ notFound: true });
  }
};

const Manifestazione = ({
  slug: _,
  site,
  header,
  footer,
  seoData,
  needHelp,
  cmsContent,
  mightLikes,
  sidebarInstantGame,
  allCarrelloErrors,
  manifestazioneDatoCms,
  ...props
}: ManifestazionePageProps) => {
  useVisualComposerSnaphot(props);
  const { seoMetaTags, canonical } = cmsContent?.sportDisciplinaPage ?? {};

  return (
    <Layout
      header={header}
      footer={footer}
      needHelp={needHelp}
      mightLikes={mightLikes}
      sidebarInstantGame={sidebarInstantGame}
    >
      <Seo siteSeo={site} metaTags={seoMetaTags} canonicalUrl={canonical} />
      <AmbienteContext.Provider
        value={{
          pathScommessa: ['sport', 'scommessa'],
          ambiente: 'sport',
          infoAggUpdateAllActionType: sportSlice.actions.updateAllInfoAggManifestazioni.type,
        }}
      >
        <PageContent
          {...cmsContent}
          seoData={seoData}
          allCarrelloErrors={allCarrelloErrors}
          sportManifestazionePage={manifestazioneDatoCms}
        />
      </AmbienteContext.Provider>
    </Layout>
  );
};

export default Manifestazione;
